import { useEditor } from "@grapesjs/react";
import { Badge, BootstrapSizes, Button, ButtonColors, LightTable, Modal } from "@talentech/components";
import { useEffect, useState } from "react";
import { useGetFontsQuery, useUploadFontMutation, useDeleteFontMutation } from "@/redux/reducer/fonts.api";
import { CustomCommands } from "@/shared/utils";
import { FontProperty } from "./FontSelectorModal";
import { toast } from "react-toastify";
import ConfirmationModal from "@/components/ConfirmationModal/ConfirmationModal";
interface IFontResponse {
  id: string;
  name: string;
  slug: string;
  variants: {
    filename: string;
    format: string;
    weight: number;
    italic: boolean;
    url: string;
  }[];
  css_url: string;
}

export default function CustomFontModal() {
  const editor = useEditor();
  const [show, setShow] = useState(false);
  const { data: fontsData } = useGetFontsQuery();
  const [uploadFont] = useUploadFontMutation();
  const [deleteFont] = useDeleteFontMutation();
  const [selectedFont, setSelectedFont] = useState<IFontResponse | null>(null);
  const [addedFonts, setAddedFonts] = useState<Set<string>>(new Set());
  const [fontName, setFontName] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [configs, setConfigs] = useState<{ weight: number; italic: boolean }[]>([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  useEffect(() => {
    editor.Commands.add(CustomCommands.MODAL_CUSTOM_FONT_OPEN, () => {
      setShow(true);
      const ds = editor.DataSources.get("custom-fonts");
      if (ds) {
        setAddedFonts(new Set(ds.getRecords().map((r) => r.get("family"))));
      }
    });
    editor.Commands.add(CustomCommands.MODAL_CUSTOM_FONT_CLOSE, () => setShow(false));
  }, [editor, show]);

  const fonts = fontsData || [];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    setFiles(newFiles);
    setConfigs(newFiles.map(() => ({ weight: 400, italic: false })));
  };

  const updateConfig = (index: number, field: "weight" | "italic", value: number | boolean) => {
    const newConfigs = [...configs];
    if (field === "weight" && typeof value === "number") {
      newConfigs[index].weight = value;
    } else if (field === "italic" && typeof value === "boolean") {
      newConfigs[index].italic = value;
    }
    setConfigs(newConfigs);
  };

  const handleUpload = async () => {
    if (!fontName || files.length === 0) {
      toast.warn("Please provide font name and files");
      return;
    }
    const font_config: { [filename: string]: { weight: number; italic: boolean } } = {};
    files.forEach((file, i) => {
      font_config[file.name] = configs[i];
    });

    const formData = new FormData();

    formData.append('font_name', fontName);
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('font_config', JSON.stringify(font_config));

    toast.promise(
      (async () => {
        const res = await uploadFont(formData).unwrap();
        const newFont = res.data || res as IFontResponse;
        setSelectedFont(newFont);
        setFontName("");
        setFiles([]);
        setConfigs([]);
      })(),
      {
        pending: "Uploading font...",
        success: "Font uploaded and added to project",
        error: "Error uploading font"
      }
    );
  };

  const addToProject = (font: IFontResponse) => {
    const family = font.name;
    const url = font.css_url;

    const insertLink = () => {
      const currentDoc = editor.Canvas.getDocument();
      if (!currentDoc || !currentDoc.head) return;
      const headEl = currentDoc.head;
      if (!headEl.querySelector(`link[href="${url}"]`)) {
        const linkEl = currentDoc.createElement("link");
        linkEl.setAttribute("rel", "stylesheet");
        linkEl.setAttribute("href", url);
        linkEl.setAttribute("data-managed", "custom-font");
        headEl.appendChild(linkEl);
      }
    };

    const doc = editor.Canvas.getDocument();
    if (!doc || !doc.head) {
      const onceLoad = () => {
        insertLink();
        editor.off("canvas:frame:load", onceLoad);
      };
      editor.on("canvas:frame:load", onceLoad);
    } else {
      insertLink();
    }

    const fontProp = editor.StyleManager.getProperty("typography", "font-family") as FontProperty | undefined;
    if (fontProp) {
      const options = fontProp.getOptions();
      if (!options.some((opt) => opt.id.toLowerCase() === family.toLowerCase())) {
        fontProp.setOptions(options.concat({ id: family, label: family }));
      }
    }

    editor.StyleManager.render(); // Add this

    let ds = editor.DataSources.get("custom-fonts");
    if (!ds) {
      ds = editor.DataSources.add({ id: "custom-fonts", records: [] });
    }
    const record = ds.getRecords().find((r) => r.get("family") === family);
    const fontData = {
      id: font.slug || family.toLowerCase().replace(/ /g, "-"),
      family,
      url,
      subsets: "latin",
      variants: (font.variants || []).map((v) => `${v.weight}${v.italic ? "italic" : ""}`).join(","),
    };
    if (record) {
      record.set(fontData);
    } else {
      ds.addRecord(fontData);
    }

    setAddedFonts((prev) => new Set([...prev, family]));
  };

  const removeFromProject = (family: string) => {
    const ds = editor.DataSources.get("custom-fonts");
    if (ds) {
      const record = ds.getRecords().find((r) => r.get("family") === family);
      if (record) {
        const url = record.get("url");
        const doc = editor.Canvas.getDocument();
        if (doc) {
          const link = doc.head.querySelector(`link[href="${url}"]`);
          if (link) link.remove();
        }
        const id = record.get("id");
        if (typeof id === "string" || typeof id === "number") {
          ds.removeRecord(id);
        }
      }
    }

    const fontProp = editor.StyleManager.getProperty("typography", "font-family") as FontProperty | undefined;
    if (fontProp) {
      const options = fontProp.getOptions().filter((opt) => opt.id !== family);
      fontProp.setOptions(options);
    }

    editor.StyleManager.render(); // Add this

    setAddedFonts((prev) => {
      const newSet = new Set(prev);
      newSet.delete(family);
      return newSet;
    });
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowConfirmDelete(true);
  };

  const onConfirmDelete = () => {
    if (deleteId) {
      toast.promise(
        deleteFont(deleteId).unwrap(),
        {
          pending: "Deleting font...",
          success: {
            render: () => {
              const family = fonts.find((f) => f.id === deleteId)?.name;
              if (family && addedFonts.has(family)) {
                removeFromProject(family);
              }
              setSelectedFont(null);
              setShowConfirmDelete(false);
              setDeleteId(null);
              return "Font deleted";
            }
          },
          error: "Error deleting font"
        }
      );
    }
  };

  const tableColumns = [
    { label: "Name", accessor: "name" },
    { 
      label: "Added", 
      accessor: "added", 
      Component: (row: IFontResponse) => {
        const isAdded = addedFonts.has(row.name);
        console.log(isAdded);
        console.log(row);
        return (
          <Badge
            label={isAdded ? "Yes" : "No"}
            color={isAdded ? "success" : "secondary"}
          />
          
        );
      }
    },
  ];

  return (
    <Modal
      title="Custom Fonts"
      showModal={show}
      onClose={() => editor.runCommand(CustomCommands.MODAL_CUSTOM_FONT_CLOSE)}
      size={BootstrapSizes.XLARGE}
      buttons={[
        <Button
          onClick={() => editor.runCommand(CustomCommands.MODAL_CUSTOM_FONT_CLOSE)}
          label="Close"
          outline
          key={"close"}
        />,
      ]}
    >
      <div className="row">
        <div className="col-md-4">
          <LightTable
            columns={tableColumns}
            data={fonts}
            onRowClick={(row) => setSelectedFont(fonts.find((f) => f.id === row.id) || null)}
          />
        </div>
        <div className="col-md-8">
          {selectedFont ? (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">{selectedFont.name}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setSelectedFont(null)}></button>
              </div>
              <ul className="list-group mb-3">
                {(selectedFont?.variants || []).map((v, i) => (
                  <li key={i} className="list-group-item">
                    {v.weight} {v.italic ? "italic" : ""} - {v.format}
                  </li>
                ))}
              </ul>
              {addedFonts.has(selectedFont.name) ? (
                <Button
                  label="Remove from project"
                  color={ButtonColors.SECONDARY}
                  onClick={() => removeFromProject(selectedFont.name)}
                  className="me-2"
                />
              ) : (
                <Button
                  label="Add to project"
                  color={ButtonColors.PRIMARY}
                  onClick={() => addToProject(selectedFont)}
                  className="me-2"
                />
              )}
              <Button
                label="Delete font"
                color={ButtonColors.DANGER}
                onClick={() => handleDelete(selectedFont.id)}
              />
            </div>
          ) : (
            <div>
              <h5>Upload New Font</h5>
              <div className="mb-3">
                <label htmlFor="fontName" className="form-label">Font Name</label>
                <input
                  id="fontName"
                  type="text"
                  className="form-control"
                  placeholder="Font Name"
                  value={fontName}
                  onChange={(e) => setFontName(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="fontFiles" className="form-label">Select Font Files</label>
                <input
                  id="fontFiles"
                  type="file"
                  multiple
                  accept=".ttf,.otf,.woff,.woff2"
                  onChange={handleFileChange}
                  className="form-control"
                />
              </div>
              {files.map((file, i) => (
                <div key={i} className="mb-3">
                  <label className="form-label">{file.name}</label>
                  <div className="row align-items-center">
                    <div className="col-auto">
                      <select
                        value={configs[i]?.weight}
                        onChange={(e) => updateConfig(i, "weight", parseInt(e.target.value))}
                        className="form-select"
                      >
                        {[100, 200, 300, 400, 500, 600, 700, 800, 900].map((w) => (
                          <option key={w} value={w}>
                            {w}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-auto">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          checked={configs[i]?.italic}
                          onChange={(e) => updateConfig(i, "italic", e.target.checked)}
                          className="form-check-input"
                          id={`italic-${i}`}
                        />
                        <label className="form-check-label" htmlFor={`italic-${i}`}>Italic</label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <Button
                label="Upload"
                color={ButtonColors.PRIMARY}
                onClick={handleUpload}
                disabled={!fontName || files.length === 0}
              />
            </div>
          )}
        </div>
      </div>
      {showConfirmDelete && (
        <ConfirmationModal
          show={showConfirmDelete}
          onClose={() => {
            setShowConfirmDelete(false);
            setDeleteId(null);
          }}
          onConfirm={onConfirmDelete}
          title="Confirm Deletion"
          children={<p>Are you sure you want to delete this font?</p>}
        />
      )}
    </Modal>
  );
}
